import React, { useEffect, useCallback, useMemo, useState } from "react";
import { FaTimes } from "react-icons/fa";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import { useTeamEditState, useTeamEditData, useTeamEditActions, teamEditRpcMessage } from "./hooks";
import {
  BowlerCard,
  ChangeBowlerModal,
  FormActions,
  LoadingStates,
  TeamLaneCard,
  TeamNameCard,
  TeamSquadCard,
  remapTeamSidePots,
} from "./components";
import { useAllEventManagement } from "../../components/createTeam/hooks/useAllEventManagement";
import { notify } from "../../components/UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../utility/errorSanitization";
import { IconButton } from "../../components/UI/DesignStandards";
import { listBracketProducts, subscribeBracketProducts } from "../../utility/brackets/api";
import {
  productsEnabledForSquad,
} from "../../utility/brackets/entryTickets";

const changeBowlerBlockReason = (bowler, scoresVerified) => {
  if (scoresVerified) {
    return "Unverify scores on Scoring before you change who is on this team.";
  }
  if (bowler?.isVerified) {
    return "Unverify this average on Averages before you change bowler.";
  }
  return null;
};

const TeamEdit = () => {
  // Custom hooks for state management, data fetching, and actions
  const {
    tournamentId,
    teamId,
    user,
    loading,
    setLoading,
    team,
    setTeam,
    squads,
    setSquads,
    tournamentEvents,
    setTournamentEvents,
    formData,
    setFormData,
    breadcrumbItems,
  } = useTeamEditState();

  // Data fetching
  const { reloadTeam } = useTeamEditData(
    tournamentId,
    teamId,
    setLoading,
    setTeam,
    setSquads,
    setTournamentEvents,
    setFormData
  );

  // Get team's primary event for all event logic
  const selectedEvent = team?.eventsID
    ? { id: team.eventsID, isPrimary: true }
    : null;
  const allEventConfigs = team?.allEventConfigs || [];

  // All event management hook
  const { loadBowlerAllEventStatus, getAllEventStatus } = useAllEventManagement(
    tournamentId,
    allEventConfigs,
    selectedEvent,
    setFormData
  );

  // Function to reload all event status for all bowlers
  const reloadAllEventStatus = useCallback(async () => {
    if (
      !tournamentId ||
      !allEventConfigs.length ||
      !formData.bowlers.length ||
      !selectedEvent
    ) {
      return;
    }

    const updates = [];
    for (let i = 0; i < formData.bowlers.length; i++) {
      const bowler = formData.bowlers[i];
      if (bowler.id && !String(bowler.id).startsWith("new-")) {
        try {
          const allEventStatus = await loadBowlerAllEventStatus(bowler.id);
          if (allEventStatus) {
            updates.push({ index: i, allEventStatus });
          }
        } catch (error) {
          console.error(
            `Error loading all event status for bowler ${bowler.id}:`,
            error
          );
        }
      }
    }

    // Apply all updates at once
    if (updates.length > 0) {
      setFormData((prev) => {
        const updated = { ...prev };
        updated.bowlers = [...updated.bowlers];
        updates.forEach(({ index, allEventStatus }) => {
          updated.bowlers[index] = {
            ...updated.bowlers[index],
            allEventStatus,
          };
        });
        return updated;
      });
    }
  }, [
    tournamentId,
    allEventConfigs.length,
    selectedEvent?.id,
    formData.bowlers,
    loadBowlerAllEventStatus,
  ]);

  // Load all event status for bowlers when they're loaded
  useEffect(() => {
    reloadAllEventStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tournamentId,
    allEventConfigs.length,
    selectedEvent?.id,
    formData.bowlers.map((b) => b.id).filter(Boolean).join(","),
    loadBowlerAllEventStatus,
  ]);

  // Actions
  const {
    handleInputChange,
    handlePaidEventChange,
    handleTicketChange,
    averageErrors,
    handleSubmit: originalHandleSubmit,
    handleCancel,
    replaceBowler,
    moveSquad,
    saveStartingLane,
    saveTeamName,
  } = useTeamEditActions(
    tournamentId,
    teamId,
    user,
    formData,
    setFormData,
    team?.squadID
  );

  // Use the original handleSubmit - reload happens when page is re-opened
  const handleSubmit = originalHandleSubmit;
  const [changeBowlerIndex, setChangeBowlerIndex] = useState(null);
  const [bracketProducts, setBracketProducts] = useState([]);

  useEffect(() => {
    if (!tournamentId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const products = await listBracketProducts(tournamentId);
        if (!cancelled) setBracketProducts(products || []);
      } catch {
        if (!cancelled) setBracketProducts([]);
      }
    };
    load();
    return subscribeBracketProducts(tournamentId, () => {
      load();
    });
  }, [tournamentId]);

  const squadBracketProducts = useMemo(
    () => productsEnabledForSquad(bracketProducts, team?.squadID),
    [bracketProducts, team?.squadID]
  );

  const handleTeamNameSave = useCallback(
    async (nextName) => {
      try {
        const savedName = await saveTeamName(nextName);
        setTeam((prev) => (prev ? { ...prev, teamName: savedName } : prev));
        notify.success("Team name saved.");
      } catch (error) {
        if (error?.code !== "INVALID_TEAM_NAME") {
          notify.error(sanitizeOperationError.update(error, "team name", false));
        }
        throw error;
      }
    },
    [saveTeamName, setTeam]
  );

  const handleLaneSave = useCallback(
    async (laneInput) => {
      try {
        const nextLane = await saveStartingLane(laneInput, team);
        setTeam((prev) => ({
          ...prev,
          startingPair: nextLane.startingPair,
          startingLane: nextLane.startingLane,
          slot: nextLane.slot,
        }));
        notify.success("Starting lane saved.");
      } catch (error) {
        if (error?.code === "PAIR_FULL") {
          notify.error(error.message);
        } else {
          notify.error(
            teamEditRpcMessage(
              error,
              sanitizeOperationError.save(error, "starting lane", false)
            )
          );
        }
        throw error;
      }
    },
    [saveStartingLane, setTeam, team]
  );

  const handleSquadSave = useCallback(
    async (nextSquadId) => {
      if (!nextSquadId || nextSquadId === team?.squadID) return;
      try {
        await moveSquad(nextSquadId);
        notify.success("Squad saved.");
        try {
          await reloadTeam();
        } catch {
          notify.error(
            "Squad saved, but Edit Team could not be reloaded. Refresh the page."
          );
        }
      } catch (error) {
        notify.error(
          teamEditRpcMessage(
            error,
            sanitizeOperationError.save(error, "squad", false)
          )
        );
        throw error;
      }
    },
    [moveSquad, reloadTeam, team?.squadID]
  );

  const handleChangeBowlerConfirm = useCallback(
    async (selectedBowler) => {
      if (changeBowlerIndex == null) return;
      const current = formData.bowlers[changeBowlerIndex];
      if (changeBowlerBlockReason(current, team?.scoresVerified)) return;
      try {
        await replaceBowler(
          current.originalBowlerId || current.id,
          selectedBowler
        );
        notify.success("Bowler changed.");
        setChangeBowlerIndex(null);
        try {
          await reloadTeam();
        } catch {
          notify.error(
            "Bowler changed, but Edit Team could not be reloaded. Refresh the page."
          );
        }
      } catch (error) {
        notify.error(
          teamEditRpcMessage(
            error,
            sanitizeOperationError.update(error, "bowler", false)
          )
        );
        throw error;
      }
    },
    [
      changeBowlerIndex,
      formData.bowlers,
      reloadTeam,
      replaceBowler,
      team?.scoresVerified,
    ]
  );

  // Show loading or error states
  const loadingState = LoadingStates({ loading, teamId, tournamentId });
  if (loadingState) return loadingState;

  const teamName = team?.teamName;
  const eligibleSquads = squads.filter(
    (squad) =>
      squad.id === team?.squadID ||
      (team?.eventsID && (squad.eventIds || []).includes(team.eventsID))
  );
  const currentSquad =
    eligibleSquads.find((squad) => squad.id === team?.squadID) ||
    squads.find((squad) => squad.id === team?.squadID) ||
    null;
  const sidePotsForSquad = tournamentEvents.filter(
    (event) => !event.templateId || event.squadId === team?.squadID
  );

  return (
    <div>
      <div className="flex justify-center mb-3 sm:mb-4 px-4 sm:px-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="card-board overflow-hidden">
        <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                Edit Team
              </h1>
              <p className="text-sm text-brand-mist mt-1">
                {teamName
                  ? teamName
                  : "Change the bowlers and side pots."}
              </p>
            </div>
            <IconButton
              type="button"
              onClick={handleCancel}
              icon={FaTimes}
              variant="ghost"
              size="sm"
              className="!text-brand-cream !bg-white/15 hover:!bg-white/25"
              aria-label="Cancel editing team"
            />
          </div>
        </div>

        <div className="p-6 space-y-6">
          <TeamNameCard
            teamName={teamName || ""}
            lineupSize={formData.bowlers.length}
            onSave={handleTeamNameSave}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TeamSquadCard
              squad={currentSquad}
              squads={eligibleSquads}
              droppedNamesForSquad={(nextSquadId) =>
                remapTeamSidePots(
                  formData.bowlers,
                  tournamentEvents,
                  nextSquadId
                ).droppedNames
              }
              onSave={handleSquadSave}
            />
            <TeamLaneCard team={team} onSave={handleLaneSave} />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h2 className="heading-display text-lg">Team Bowlers</h2>
              <p className="text-sm text-brand-muted">
                Change bowler puts a different person in this place. Save updates
                contact, average, side pots, and bracket tickets.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {formData.bowlers.map((bowler, index) => (
                <BowlerCard
                  key={bowler.originalBowlerId || bowler.id}
                  bowler={bowler}
                  index={index}
                  tournamentEvents={sidePotsForSquad}
                  onInputChange={handleInputChange}
                  onPaidEventChange={handlePaidEventChange}
                  bracketProducts={squadBracketProducts}
                  onTicketChange={handleTicketChange}
                  squadId={team?.squadID}
                  allEventConfigs={allEventConfigs}
                  allEventStatus={bowler.allEventStatus || null}
                  selectedEvent={selectedEvent}
                  getAllEventStatus={getAllEventStatus}
                  currentTeamId={teamId}
                  onChangeBowler={(index) => {
                    const reason = changeBowlerBlockReason(
                      formData.bowlers[index],
                      team?.scoresVerified
                    );
                    if (reason) return;
                    setChangeBowlerIndex(index);
                  }}
                  changeBowlerBlockReason={changeBowlerBlockReason(
                    bowler,
                    team?.scoresVerified
                  )}
                  averageError={averageErrors[index] || ""}
                />
              ))}
            </div>

            <FormActions onCancel={handleCancel} loading={loading} />
          </form>
        </div>
      </div>

      <ChangeBowlerModal
        isOpen={changeBowlerIndex != null}
        onClose={() => setChangeBowlerIndex(null)}
        onConfirm={handleChangeBowlerConfirm}
        currentBowler={
          changeBowlerIndex != null ? formData.bowlers[changeBowlerIndex] : null
        }
        takenBowlerIds={formData.bowlers
          .filter((_, index) => index !== changeBowlerIndex)
          .map((bowler) => bowler.id)
          .filter((id) => id && !String(id).startsWith("new-"))}
        tournamentId={tournamentId}
      />
    </div>
  );
};

export default TeamEdit;
